import { useEffect, useRef, useState } from 'preact/hooks';
import type { UiCopy } from '../copy';
import { uiCopy } from '../copy';
import { IconArrows } from './icons';

interface Props {
  before: string;
  after: string;
  /** Breedte / hoogte van het resultaat. */
  ratio: number;
  /** Naam van de gekozen stijl, rechts op de vergelijking. */
  styleLabel: string;
  copy?: UiCopy;
}

/**
 * Voor/na-schuif. Links de originele foto, rechts de aquarel. Opent met een korte veegbeweging,
 * zodat meteen duidelijk is dat je kunt schuiven. Werkt met aanraken, muis en pijltjestoetsen.
 */
export function Compare({ before, after, ratio, styleLabel, copy = uiCopy(undefined) }: Props) {
  const [pos, setPos] = useState(100);
  const box = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  useEffect(() => {
    let raf = 0;
    const start = window.setTimeout(() => {
      const t0 = performance.now();
      const step = (t: number) => {
        const k = Math.min(1, (t - t0) / 900);
        const eased = 1 - Math.pow(1 - k, 3);
        setPos(100 - 50 * eased);
        if (k < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, 350);
    return () => {
      window.clearTimeout(start);
      cancelAnimationFrame(raf);
    };
  }, [after]);

  const moveTo = (clientX: number) => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    setPos(Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={box}
      class="compare"
      style={{ aspectRatio: `${ratio}`, maxHeight: '58dvh' }}
      role="slider"
      tabIndex={0}
      aria-label={copy.compareAria}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      aria-valuetext={copy.photoVisible(Math.round(pos))}
      onPointerDown={(e) => {
        dragging.current = true;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        moveTo(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && moveTo(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 5));
        if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + 5));
      }}
    >
      <img src={after} alt={copy.resultAlt} draggable={false} />
      <img src={before} alt={copy.originalAlt} draggable={false} style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
      <div class="compare__handle" style={{ left: `${pos}%` }}>
        <div class="compare__line" />
        <div class="compare__knob">
          <IconArrows />
        </div>
      </div>
      <span class="chip chip--l">{copy.photoChip}</span>
      <span class="chip chip--r">{styleLabel}</span>
    </div>
  );
}
